import { DOCUMENT, LOCATION_INITIALIZED } from "@angular/common";
import { EnvironmentProviders, inject, Injector, provideAppInitializer } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom, tap } from "rxjs";
import { CookieStorageService } from "src/app/shared/services/cookie-storage.service";
import { environment } from "src/environments/environment";
import { AppSettings } from "../config/app-settings.config";
import { isDevelopment } from "../constants/is-development";
import { getLanguageDirection } from "../constants/language-direction";
import { APP_TRANSLATION_TEMPLATE } from "../constants/translate";
import { AppLanguage } from "../enums/app-language.enum";
import { EnvironmentId } from "../enums/environment-id.enum";
import { arrayShallowClone } from "../functions/array-helpers";
import { getHtmlLangValue } from "../functions/get-html-lang-value";
import { joinPromiseRecord } from "../functions/join-promise-record";
import { langTagToAppLang } from "../functions/lang-tag-to-app-lang";

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const importTemplateTranslation = () => import("public/assets/i18n/template.json").then((m) => m.default);
const importEnTranslation = () => import("public/assets/i18n/en.json").then((m) => m.default);
/* eslint-enable @typescript-eslint/explicit-function-return-type */

interface DefaultTranslationOptions {
  /** load a user default language based on their browser's setting after loading the app's default for translation fallbacks */
  pickUserDefault?: boolean;
}

// https://github.com/ngx-translate/core/issues/517#issuecomment-299637956
export function provideAppDefaultTranslation(options?: DefaultTranslationOptions): EnvironmentProviders {
  return provideAppInitializer(() => loadDefaultTranslationFn(options).catch((err: unknown) => console.error(err)));
}

async function loadDefaultTranslationFn(options?: DefaultTranslationOptions): Promise<void> {
  const injector = inject(Injector);

  await injector.get(LOCATION_INITIALIZED, Promise.resolve());

  const document = injector.get(DOCUMENT, window.document);
  const translate = injector.get(TranslateService);
  const cookieStorage = injector.get(CookieStorageService);

  try {
    // Lazily import and store english defaults (Don't use template.json as an language, it isn't one)
    // Use template.json as English on Dev, otherwise it's just a fallback
    const { templateTranslation, mainEnglishTranslation } = await joinPromiseRecord({
      templateTranslation: importTemplateTranslation(),
      mainEnglishTranslation: environment.id === EnvironmentId.Development ? undefined : importEnTranslation(),
    });
    translate.setTranslation(APP_TRANSLATION_TEMPLATE, templateTranslation);
    translate.setTranslation(AppLanguage.EnglishUnitedStates, mainEnglishTranslation ?? templateTranslation);
  } catch (err: unknown) {
    console.error(err);
  }

  const appFallbackLang = translate.getFallbackLang() || AppSettings.defaultLanguage;
  // Load (if not stored already), use and set Default Language
  translate.addLangs(Object.values(AppLanguage).filter((x) => x && typeof x === "string"));
  document.documentElement.lang = getHtmlLangValue(appFallbackLang);
  document.dir = getLanguageDirection(appFallbackLang);

  // load the default language for translation fallback
  await useLang(translate, appFallbackLang);

  // to change language if it's on url's query params
  const url = new URL(window.location.href);
  const paramLangCodes: ReadonlyArray<string> = url.searchParams.getAll("lang")?.flatMap((x) => x?.split(/(\s|\+|,)+/g) ?? []);
  if (paramLangCodes?.length > 0) {
    await useLangFromLangTagList(translate, paramLangCodes);
  } else if (options?.pickUserDefault) {
    // pick a language based on the user's browser language and region
    const userLangList = navigator.languages ? arrayShallowClone(navigator.languages) : [navigator.language];
    const guestLang = cookieStorage.guestLang();
    if (guestLang) {
      // Refresh Expire Date
      cookieStorage.setGuestLang(guestLang);
      userLangList.unshift(guestLang);
    }
    await useLangFromLangTagList(translate, userLangList);
  }

  const currentLang = translate.getCurrentLang()!;
  document.documentElement.lang = getHtmlLangValue(currentLang);
  document.dir = getLanguageDirection(currentLang);
}

async function useLang(translate: TranslateService, langCode: string): Promise<void> {
  try {
    await firstValueFrom(
      translate.use(langCode).pipe(tap(() => isDevelopment && console.log(`✅ Successfully initialized '${langCode}' language.`)))
    );
  } catch (err: unknown) {
    console.error(`'${langCode}' not available.`);
    throw err;
  }
}

async function useLangFromLangTagList(translate: TranslateService, langTags: ReadonlyArray<string>): Promise<void> {
  for (const langTag of langTags) {
    const appLanguage = langTagToAppLang(langTag);
    if (appLanguage) {
      if (appLanguage !== translate.getCurrentLang()) {
        try {
          await useLang(translate, appLanguage);
        } catch (err: unknown) {
          if (err instanceof Error && err.message) {
            console.error(err.message);
          }
        }
      }
      break;
    }
  }
}
